import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * `.card` verbatim (tokens doc §7.4): white, 1px neutral-200, 16px radius and the
 * almost-imperceptible `--shadow-card`. The border does the structural work; the shadow
 * only stops the card dissolving into the canvas.
 *
 * The card owns no padding — CardHeader / CardBody / CardFooter do, so a Table can sit
 * flush inside the same shell (§10.3).
 */
export function Card({ className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-neutral-200 bg-surface shadow-card',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  /** The `.card-subtitle` lede: 14/400/neutral-500. */
  subtitle?: ReactNode;
  /** Right-aligned actions, e.g. a filter or an "Add" button. */
  actions?: ReactNode;
  /** Heading level. Defaults to h2 — the page's h1 belongs to PageHeader. */
  as?: 'h1' | 'h2' | 'h3';
}

export function CardHeader({
  title,
  subtitle,
  actions,
  as: Heading = 'h2',
  className,
  ...rest
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 px-6 pt-6 max-[480px]:px-4 max-[480px]:pt-5',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0">
        <Heading className="text-xl font-bold text-ink">{title}</Heading>
        {subtitle === undefined ? null : (
          <p className="mt-1 text-base leading-[21px] text-neutral-500">{subtitle}</p>
        )}
      </div>
      {actions === undefined ? null : <div className="shrink-0">{actions}</div>}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** 24px desktop, 20px/16px below 480px — the funnel's card padding. */
export function CardBody({ className, children, ...rest }: CardBodyProps) {
  return (
    <div className={cn('p-6 max-[480px]:px-4 max-[480px]:py-5', className)} {...rest}>
      {children}
    </div>
  );
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardFooter({ className, children, ...rest }: CardFooterProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-3 border-t border-neutral-100 px-6 py-4 max-[480px]:px-4',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
